import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useMembership } from "@/hooks/useMembership";
import { ShieldAlert, ClipboardCheck, Trash2, Download } from "lucide-react";

/**
 * Temporary admin-only Stripe Test Mode checklist. Editable table persisted to
 * localStorage so Jana + the AI can co-track results across sessions. Remove
 * this route after launch sign-off — it's diagnostic only. Contains no PII
 * beyond the tester's own notes.
 */

export const Route = createFileRoute("/admin_/stripe-test-checklist")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Admin — Stripe Test Mode Checklist" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: ChecklistPage,
});

type Status = "pending" | "in_progress" | "pass" | "fail" | "blocked";

type Row = {
  id: string;
  scenario: string;
  testUser: string;
  expected: string;
  actual: string;
  membershipStatus: string;
  stripeEvent: string;
  status: Status;
  notes: string;
};

const STORAGE_KEY = "rt_stripe_test_checklist_v1";

const DEFAULT_ROWS: Row[] = [
  {
    id: "1",
    scenario: "New user signup + email verification",
    testUser: "",
    expected: "profile + membership(email_unverified) created; verification email received",
    actual: "",
    membershipStatus: "email_unverified → registered_unpaid",
    stripeEvent: "—",
    status: "pending",
    notes: "",
  },
  {
    id: "2",
    scenario: "Checkout session creation",
    testUser: "",
    expected: "Stripe Checkout URL returned, checkout_attempt_id stored",
    actual: "",
    membershipStatus: "registered_unpaid",
    stripeEvent: "—",
    status: "pending",
    notes: "",
  },
  {
    id: "3",
    scenario: "Successful payment (card 4242 4242 4242 4242)",
    testUser: "",
    expected: "membership.status=active, current_period_end set, dashboard accessible",
    actual: "",
    membershipStatus: "active",
    stripeEvent: "checkout.session.completed + customer.subscription.created + invoice.paid",
    status: "pending",
    notes: "",
  },
  {
    id: "4",
    scenario: "Abandoned checkout + retry",
    testUser: "",
    expected: "First session closed; retry produces a fresh Checkout URL; only one active membership row",
    actual: "",
    membershipStatus: "registered_unpaid → active on retry success",
    stripeEvent: "checkout.session.expired then completed on retry",
    status: "pending",
    notes: "",
  },
  {
    id: "5",
    scenario: "Failed payment (card 4000 0000 0000 0002)",
    testUser: "",
    expected: "Payment declined, membership stays registered_unpaid, error surfaced",
    actual: "",
    membershipStatus: "registered_unpaid",
    stripeEvent: "payment_intent.payment_failed",
    status: "pending",
    notes: "",
  },
  {
    id: "6",
    scenario: "Payment recovery (retry with 4242 after decline)",
    testUser: "",
    expected: "Second attempt succeeds, membership activates",
    actual: "",
    membershipStatus: "active",
    stripeEvent: "checkout.session.completed",
    status: "pending",
    notes: "",
  },
  {
    id: "7",
    scenario: "Subscription cancellation (immediate)",
    testUser: "",
    expected: "membership.status=cancelled, dashboard locked",
    actual: "",
    membershipStatus: "cancelled",
    stripeEvent: "customer.subscription.deleted",
    status: "pending",
    notes: "",
  },
  {
    id: "8",
    scenario: "Cancel at period end",
    testUser: "",
    expected: "cancel_at_period_end=true, still active until period_end",
    actual: "",
    membershipStatus: "active (cancel_at_period_end)",
    stripeEvent: "customer.subscription.updated",
    status: "pending",
    notes: "",
  },
  {
    id: "9",
    scenario: "Login after activation",
    testUser: "",
    expected: "Sign in → straight to dashboard, no LaunchLockGate",
    actual: "",
    membershipStatus: "active",
    stripeEvent: "—",
    status: "pending",
    notes: "",
  },
  {
    id: "10",
    scenario: "Unpaid user LaunchLockGate behaviour",
    testUser: "",
    expected: "registered_unpaid user hits gate, sees CTA to complete payment",
    actual: "",
    membershipStatus: "registered_unpaid",
    stripeEvent: "—",
    status: "pending",
    notes: "",
  },
  {
    id: "11",
    scenario: "Admin Stripe Operations page loads",
    testUser: "",
    expected: "Snapshot, subscription, invoices, webhook history render",
    actual: "",
    membershipStatus: "n/a",
    stripeEvent: "—",
    status: "pending",
    notes: "",
  },
  {
    id: "12",
    scenario: "Webhook deduplication (replay same event id)",
    testUser: "AI-automated",
    expected: "Second delivery is a no-op; processed_stripe_events unchanged count",
    actual: "",
    membershipStatus: "unchanged",
    stripeEvent: "duplicate checkout.session.completed",
    status: "pending",
    notes: "",
  },
  {
    id: "13",
    scenario: "Out-of-order webhook protection",
    testUser: "AI-automated",
    expected: "Old subscription.updated cannot downgrade newer active subscription",
    actual: "",
    membershipStatus: "active (unchanged)",
    stripeEvent: "stale customer.subscription.updated",
    status: "pending",
    notes: "",
  },
];

const STATUS_COLORS: Record<Status, string> = {
  pending: "bg-muted text-muted-foreground",
  in_progress: "bg-amber-100 text-amber-900",
  pass: "bg-emerald-100 text-emerald-900",
  fail: "bg-red-100 text-red-900",
  blocked: "bg-muted text-foreground",
};

function ChecklistPage() {
  const { loading, isAdmin } = useMembership();
  const [rows, setRows] = useState<Row[]>(DEFAULT_ROWS);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Row[];
        // Merge — keep new scenarios if we've added any since save.
        const byId = new Map(parsed.map((r) => [r.id, r]));
        setRows(DEFAULT_ROWS.map((d) => byId.get(d.id) ?? d));
      }
    } catch {
      /* ignore */
    }
    setReady(true);
  }, []);

  useEffect(() => {
    if (!ready) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(rows));
    } catch {
      /* ignore */
    }
  }, [rows, ready]);

  const summary = useMemo(() => {
    const c = { pass: 0, fail: 0, pending: 0, in_progress: 0, blocked: 0 };
    for (const r of rows) c[r.status]++;
    return c;
  }, [rows]);

  function update(id: string, patch: Partial<Row>) {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }

  function reset() {
    if (confirm("Reset all checklist entries?")) setRows(DEFAULT_ROWS);
  }

  function exportJson() {
    const blob = new Blob([JSON.stringify(rows, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `stripe-test-checklist-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (loading) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;
  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-lg p-8 text-center">
        <ShieldAlert className="mx-auto h-10 w-10 text-destructive" />
        <h1 className="mt-3 font-display text-2xl">Admin only</h1>
        <Link to="/" className="mt-4 inline-block text-sm text-[var(--gold-deep)] underline">
          Back home
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1400px] p-4 sm:p-8">
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-display text-2xl text-foreground">
            <ClipboardCheck className="h-6 w-6 text-[var(--gold-deep)]" />
            Stripe Test Mode Checklist
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Temporary — remove after launch sign-off. Autosaves locally.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className={`rounded-full px-2 py-1 ${STATUS_COLORS.pass}`}>PASS {summary.pass}</span>
          <span className={`rounded-full px-2 py-1 ${STATUS_COLORS.fail}`}>FAIL {summary.fail}</span>
          <span className={`rounded-full px-2 py-1 ${STATUS_COLORS.in_progress}`}>In progress {summary.in_progress}</span>
          <span className={`rounded-full px-2 py-1 ${STATUS_COLORS.pending}`}>Pending {summary.pending}</span>
          <span className={`rounded-full px-2 py-1 ${STATUS_COLORS.blocked}`}>Blocked {summary.blocked}</span>
          <button
            onClick={exportJson}
            className="ms-2 inline-flex items-center gap-1 rounded-md border border-input px-2 py-1 hover:bg-muted"
          >
            <Download className="h-3.5 w-3.5" /> Export
          </button>
          <button
            onClick={reset}
            className="inline-flex items-center gap-1 rounded-md border border-destructive/40 px-2 py-1 text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="h-3.5 w-3.5" /> Reset
          </button>
        </div>
      </header>

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full min-w-[1200px] text-xs">
          <thead className="bg-muted/60">
            <tr className="text-start">
              <th className="p-2">#</th>
              <th className="p-2">Scenario</th>
              <th className="p-2">Test user</th>
              <th className="p-2">Expected</th>
              <th className="p-2">Actual</th>
              <th className="p-2">Membership status</th>
              <th className="p-2">Stripe event</th>
              <th className="p-2">Status</th>
              <th className="p-2">Notes</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-border align-top">
                <td className="p-2 font-mono">{r.id}</td>
                <td className="p-2 font-medium">{r.scenario}</td>
                <td className="p-2">
                  <input
                    value={r.testUser}
                    onChange={(e) => update(r.id, { testUser: e.target.value })}
                    placeholder="email or —"
                    className="w-40 rounded border border-input bg-background px-1.5 py-1"
                  />
                </td>
                <td className="p-2 text-muted-foreground">{r.expected}</td>
                <td className="p-2">
                  <textarea
                    value={r.actual}
                    onChange={(e) => update(r.id, { actual: e.target.value })}
                    rows={2}
                    className="w-52 rounded border border-input bg-background px-1.5 py-1"
                  />
                </td>
                <td className="p-2">
                  <input
                    value={r.membershipStatus}
                    onChange={(e) => update(r.id, { membershipStatus: e.target.value })}
                    className="w-40 rounded border border-input bg-background px-1.5 py-1"
                  />
                </td>
                <td className="p-2 text-muted-foreground">{r.stripeEvent}</td>
                <td className="p-2">
                  <select
                    value={r.status}
                    onChange={(e) => update(r.id, { status: e.target.value as Status })}
                    className={`rounded border border-input px-1.5 py-1 ${STATUS_COLORS[r.status]}`}
                  >
                    <option value="pending">pending</option>
                    <option value="in_progress">in_progress</option>
                    <option value="pass">PASS</option>
                    <option value="fail">FAIL</option>
                    <option value="blocked">blocked</option>
                  </select>
                </td>
                <td className="p-2">
                  <textarea
                    value={r.notes}
                    onChange={(e) => update(r.id, { notes: e.target.value })}
                    rows={2}
                    className="w-56 rounded border border-input bg-background px-1.5 py-1"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-4 text-xs text-muted-foreground">
        Data lives in localStorage under <code>{STORAGE_KEY}</code>. Export before clearing browser
        data.
      </p>
    </div>
  );
}
